/**
 * map-controls.js
 */

import React from 'react';
import { 
  useMediaQuery,
  darken,
  lighten,
  IconButton,
  ButtonGroup,
  Button,
} from '@mui/material';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu/drop-menu';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { MapContext } from './map-context';
import { MAP_TYPE } from './map-common';


const mapButtonContainerStyles = (isMobile) => {
  return {
    mr: 1.5,
    mt: 1.5,
    zIndex: 200,
    boxShadow: (theme) => theme.boxShadowMaker(...theme.palette.comp.tracking.shadowParams),
    opacity: isMobile ? 1 : 0.8,
  };
}


const mapButtonStyles = (isActive = false) => {
  return {
    color: isActive ? 'common.white' : lighten('rgba(0, 0, 0)', 0.4),
    backgroundColor: (theme) => isActive ? theme.palette.sys.primary : 'common.white',
    '&:hover': {
      backgroundColor: (theme) => isActive ? theme.palette.sys.primary : darken('rgba(255,255,255)', 0.1),
    },
  };
}


export const MapIconButton = (props) => {
  const {
    typeName,
    onClick,
    isActive,
    sxProps,
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <IconButton 
      onClick={onClick} 
      sx={{ 
        ...sxProps,
        ...mapButtonContainerStyles(isMobile),
        ...mapButtonStyles(isActive)
      }}
    >
      <Icon type={typeName} />
    </IconButton>
  );
}


export const MapTypeControl = (props) => {
  const { mapType, setMapType } = React.useContext(MapContext);

  return (
    <DropMenu
      id='map-layers'
      label={<MapIconButton typeName='layers' />}
      aria-label='map type selector'
      items={[
        {
          id: MAP_TYPE.SATELLITE,
          name: 'Satellite',
          active: mapType === MAP_TYPE.SATELLITE
        }, {
          id: MAP_TYPE.ROAD,
          name: 'Road',
          active: mapType === MAP_TYPE.ROAD,
        }, {
          id: MAP_TYPE.TERRAIN,
          name: 'Terrain',
          active: mapType === MAP_TYPE.TERRAIN,
        }
      ]}
      onSelect={(item) => {
        setMapType(item.id);
      }}
    />
  );
}


export const CurrentLocationControl = (props) => {
  const { isUserCentered, setIsUserCentered } = React.useContext(LiveTrackingContext);

  return (
    <MapIconButton 
      typeName='my-location' 
      isActive={isUserCentered}
      onClick={async () => {
        setIsUserCentered(true);
      }}
    />
  )
}


const ZoomButton = (props) => {
  return (
    <Button 
      sx={{ 
        p: '6px',
        borderRadius: '12px',
        ...mapButtonStyles()
      }}
      {...props}
    />
  )
}


export const MapZoomControl = (props) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { map } = React.useContext(MapContext);


  const setZoom = (isIn) => {
    if (map) {
      const zoom = map.getZoom();
      const newZoom = zoom + (isIn ? 1 : -1);
      map.setZoom(newZoom);
    }
  }


  return (
    <ButtonGroup
      orientation='vertical'
      variant='contained'
      aria-label='tracking zoom buttons'
      sx={{
        ...mapButtonContainerStyles(isMobile),
        borderRadius: '12px',
        '& .MuiButtonGroup-firstButton': {
          borderColor: '#d3d3d3',
        }
      }}
    >
      <ZoomButton onClick={() => setZoom(true)}><Icon type='zoom-in' /></ZoomButton>
      <ZoomButton onClick={() => setZoom(false)}><Icon type='zoom-out' /></ZoomButton>
    </ButtonGroup>
  )
}